import {
  type EoReader,
  PacketAction,
  PacketFamily,
  SpellRequestServerPacket,
  SpellTargetGroupServerPacket,
  SpellTargetOtherServerPacket,
  SpellTargetSelfServerPacket,
} from 'eolib';
import type { Client } from '@/client';
import {
  CharacterSpellChantAnimation,
  EffectTargetCharacter,
  HealthBar,
} from '@/render';

function handleSpellRequest(client: Client, reader: EoReader) {
  const packet = SpellRequestServerPacket.deserialize(reader);
  const character = client.getCharacterById(packet.playerId);
  if (!character) {
    client.sessionController.requestCharacterRange([packet.playerId]);
    return;
  }

  const record = client.getEsfRecordById(packet.spellId);
  if (!record) {
    return;
  }

  client.animationController.pendingCharacterAnimations.set(
    packet.playerId,
    new CharacterSpellChantAnimation(
      client.sans11!,
      packet.spellId,
      record.chant,
      record.castTime,
    ),
  );
}

function handleSpellTargetSelf(client: Client, reader: EoReader) {
  const packet = SpellTargetSelfServerPacket.deserialize(reader);
  if (packet.hp != null) {
    client.hp = packet.hp;
  }

  if (packet.tp != null) {
    client.tp = packet.tp;
  }

  if (packet.hp != null || packet.tp != null) {
    client.statsController.notifyStatsUpdated();
  }

  const character = client.getCharacterById(packet.playerId);
  if (!character) {
    client.sessionController.requestCharacterRange([packet.playerId]);
    return;
  }

  // Buffs have no healing amount. Do not render a misleading MISS above the caster.
  if (packet.spellHealHp > 0) {
    client.animationController.characterHealthBars.set(
      packet.playerId,
      new HealthBar(packet.hpPercentage, 0, packet.spellHealHp),
    );
  }

  client.spellController.playSpellEffect(
    packet.spellId,
    new EffectTargetCharacter(packet.playerId),
  );
}

function handleSpellTargetOther(client: Client, reader: EoReader) {
  const packet = SpellTargetOtherServerPacket.deserialize(reader);
  if (packet.hp != null) {
    client.hp = packet.hp;
    client.statsController.notifyStatsUpdated();
  }

  const caster = client.getCharacterById(packet.casterId);
  if (caster) {
    caster.direction = packet.casterDirection;
  } else {
    client.sessionController.requestCharacterRange([packet.casterId]);
  }

  const character = client.getCharacterById(packet.victimId);
  if (!character) {
    client.sessionController.requestCharacterRange([packet.victimId]);
    return;
  }

  if (packet.spellHealHp > 0) {
    client.animationController.characterHealthBars.set(
      packet.victimId,
      new HealthBar(packet.hpPercentage, 0, packet.spellHealHp),
    );
  }

  client.spellController.playSpellEffect(
    packet.spellId,
    new EffectTargetCharacter(packet.victimId),
  );
}

function handleSpellTargetGroup(client: Client, reader: EoReader) {
  const packet = SpellTargetGroupServerPacket.deserialize(reader);
  let statsUpdate = false;
  if (packet.casterId === client.playerId) {
    client.tp = packet.casterTp;
    statsUpdate = true;
  }

  const unknownPlayerIds = [];
  for (const player of packet.players) {
    if (player.playerId === client.playerId) {
      client.hp = player.hp;
      statsUpdate = true;
    }

    const character = client.getCharacterById(player.playerId);
    if (!character) {
      unknownPlayerIds.push(player.playerId);
      continue;
    }

    if (packet.spellHealHp > 0) {
      client.animationController.characterHealthBars.set(
        player.playerId,
        new HealthBar(player.hpPercentage, 0, packet.spellHealHp),
      );
    }

    client.spellController.playSpellEffect(
      packet.spellId,
      new EffectTargetCharacter(player.playerId),
    );
  }

  if (statsUpdate) {
    client.statsController.notifyStatsUpdated();
  }
}

export function registerSpellHandlers(client: Client) {
  client.bus!.registerPacketHandler(
    PacketFamily.Spell,
    PacketAction.Request,
    (reader) => handleSpellRequest(client, reader),
  );
  client.bus!.registerPacketHandler(
    PacketFamily.Spell,
    PacketAction.TargetSelf,
    (reader) => handleSpellTargetSelf(client, reader),
  );
  client.bus!.registerPacketHandler(
    PacketFamily.Spell,
    PacketAction.TargetOther,
    (reader) => handleSpellTargetOther(client, reader),
  );
  client.bus!.registerPacketHandler(
    PacketFamily.Spell,
    PacketAction.TargetGroup,
    (reader) => handleSpellTargetGroup(client, reader),
  );
}
